import { createContext, useContext, useEffect } from 'react';

import { useIntl } from 'react-intl';
import { connect } from 'react-redux';
import { NavigateFunction, useNavigate } from 'react-router-dom';

import { isNewOffersEnabled } from '@src/containers/Offers/offers/offersHelpers';
import {
    CHECK_ELIGIBILITY_STEP_ID,
    DEFAULT_STEP_ORDER,
    PRODUCT_OFFERS_STEP_ID
} from '@src/containers/Routes/helper/stepActiveIds.helper';
import {
    setNavigateTo,
    setBasePath,
    TSetNavigationAction
} from '@src/containers/Routes/Navigation.action';
import { RootState, AppDispatch } from '@src/reducers';

type TProps = {
    children: React.ReactNode;
};

type NavigationContextType = {
    basePath: string;
    navigate: NavigateFunction;
    progressIndicatorSteps: string[];
};

const NavigationContext = createContext<NavigationContextType | null>(null);

const mapStateToProps = (state: RootState) => ({
    isNewOffersEnabled: isNewOffersEnabled(state)
});

const mapDispatchToProps = (dispatch: AppDispatch) => ({
    setNavigate: (navigate: TSetNavigationAction['payload']['navigate']) => {
        dispatch(setNavigateTo(navigate));
    },
    setBasePath: (basePath: string) => {
        dispatch(setBasePath(basePath));
    }
});

const NavigationProvider = ({
    children,
    isNewOffersEnabled,
    setBasePath,
    setNavigate
}: TProps &
    ReturnType<typeof mapStateToProps> &
    ReturnType<typeof mapDispatchToProps>) => {
    const navigate = useNavigate();
    const { locale } = useIntl();
    const basePath = locale === 'en' ? '/account/signup' : '/konto/anmeldung';
    const progressStepOrder = DEFAULT_STEP_ORDER.filter(
        (stepId) => stepId !== CHECK_ELIGIBILITY_STEP_ID
    );

    useEffect(() => {
        setBasePath(basePath);
        setNavigate(navigate);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const progressIndicatorSteps = isNewOffersEnabled
        ? progressStepOrder
        : progressStepOrder.filter(
              (stepId) => stepId !== PRODUCT_OFFERS_STEP_ID
          );

    return (
        <NavigationContext.Provider
            value={{ basePath, navigate, progressIndicatorSteps }}>
            {children}
        </NavigationContext.Provider>
    );
};

export const useNavigation = () => {
    const context = useContext(NavigationContext);

    if (!context) {
        throw new Error(
            'useNavigation must be used within a NavigationProvider'
        );
    }

    return context;
};

export default connect(mapStateToProps, mapDispatchToProps)(NavigationProvider);
